/**
 * PSV-1889 — achievements (single-shot) + daily quests (date-rotated).
 *
 * Server-authoritative: the arena tracks per-ship counters, evaluates
 * unlock/progress on every relevant event, credits rewards, persists to
 * `Player.Quests` (one flat JSON text field — parts are replace-not-merge
 * on the platform, a scalar sidesteps that; PSV-1898 finding). The client
 * only renders (tracker HUD + toasts) from QuestState broadcasts.
 *
 * Daily rotation is DETERMINISTIC from the UTC date: everyone sees the
 * same 3 quests on a given day with no server state — `dailiesFor(date)`
 * picks from the pool by day-number arithmetic. Reset is implicit: a new
 * UTC date yields a new set and progress starts from zero (per-day
 * progress is keyed by the date string).
 */

export type QuestMetric =
  | 'kills'
  | 'asteroids'
  | 'crystals' // gems collected via pickups (lifetime for achievements)
  | 'tier'
  | 'surviveSec'
  | 'fullHoldKills';

export interface AchievementDef {
  id: string;
  name: string;
  desc: string;
  metric: QuestMetric;
  target: number;
}

export const ACHIEVEMENTS: readonly AchievementDef[] = [
  { id: 'ach-first-kill', name: 'First Blood', desc: 'Kill a player', metric: 'kills', target: 1 },
  { id: 'ach-rock-100', name: 'Rock Crusher', desc: 'Shatter 100 asteroids', metric: 'asteroids', target: 100 },
  { id: 'ach-crystals-500', name: 'Prospector', desc: 'Collect 500 crystals', metric: 'crystals', target: 500 },
  { id: 'ach-tier-3', name: 'Apex Pilot', desc: 'Reach tier 3', metric: 'tier', target: 3 },
  { id: 'ach-survive-600', name: 'Survivor', desc: 'Survive 10 minutes in one life', metric: 'surviveSec', target: 600 },
  { id: 'ach-full-hold-kill', name: 'Greedy Hunter', desc: 'Kill a player while your hold is full', metric: 'fullHoldKills', target: 1 },
] as const;

export interface DailyDef {
  id: string;
  name: string;
  metric: QuestMetric;
  target: number;
  rewardGems: number;
}

/** Pool the daily rotation draws from (3 per day). */
export const DAILY_POOL: readonly DailyDef[] = [
  { id: 'daily-mine-150', name: 'Mine 150 gems', metric: 'crystals', target: 150, rewardGems: 40 },
  { id: 'daily-kills-2', name: 'Score 2 kills', metric: 'kills', target: 2, rewardGems: 50 },
  { id: 'daily-rocks-20', name: 'Shatter 20 asteroids', metric: 'asteroids', target: 20, rewardGems: 30 },
  { id: 'daily-mine-400', name: 'Mine 400 gems', metric: 'crystals', target: 400, rewardGems: 90 },
  { id: 'daily-rocks-50', name: 'Shatter 50 asteroids', metric: 'asteroids', target: 50, rewardGems: 60 },
  { id: 'daily-survive-300', name: 'Survive 5 minutes', metric: 'surviveSec', target: 300, rewardGems: 40 },
] as const;

const ACH_BY_ID = new Map(ACHIEVEMENTS.map((a) => [a.id, a] as const));
const DAILY_BY_ID = new Map(DAILY_POOL.map((d) => [d.id, d] as const));

export function achievementById(id: string): AchievementDef | null {
  return ACH_BY_ID.get(id) ?? null;
}
export function dailyById(id: string): DailyDef | null {
  return DAILY_BY_ID.get(id) ?? null;
}

/** UTC date key, e.g. "2026-07-27". */
export function utcDateKey(now: number): string {
  return new Date(now).toISOString().slice(0, 10);
}

/**
 * The 3 dailies for a UTC date — identical on every client/server for the
 * same date. Day-number stride is coprime with the pool size so the mix
 * rotates through all combinations.
 */
export function dailiesFor(dateKey: string): DailyDef[] {
  const dayNumber = Math.floor(Date.parse(`${dateKey}T00:00:00Z`) / 86_400_000);
  const n = DAILY_POOL.length;
  const first = ((dayNumber % n) + n) % n;
  return [0, 1, 2].map((i) => DAILY_POOL[(first + i * 5) % n]!);
}

/**
 * Persisted quest state (Player.Quests JSON):
 *   unlocked  — achievement ids (single-shot, never repeats);
 *   lifetime  — accumulating counters driving achievements;
 *   daily     — per-UTC-date progress + which dailies were paid out.
 */
export interface QuestState {
  unlocked: string[];
  lifetime: Partial<Record<QuestMetric, number>>;
  daily: {
    date: string;
    progress: Partial<Record<QuestMetric, number>>;
    claimed: string[];
  };
}

export function emptyQuestState(now: number): QuestState {
  return {
    unlocked: [],
    lifetime: {},
    daily: { date: utcDateKey(now), progress: {}, claimed: [] },
  };
}

export function serializeQuestState(q: QuestState): string {
  return JSON.stringify(q);
}

/** Tolerant parse — garbage/missing → fresh state. */
export function parseQuestState(raw: unknown, now: number): QuestState {
  const empty = emptyQuestState(now);
  if (typeof raw !== 'string' || raw === '') return empty;
  try {
    const p = JSON.parse(raw) as Partial<QuestState> | null;
    if (p === null || typeof p !== 'object') return empty;
    const unlocked = Array.isArray(p.unlocked)
      ? p.unlocked.filter((id): id is string => typeof id === 'string' && ACH_BY_ID.has(id))
      : [];
    const lifetime =
      p.lifetime !== null && typeof p.lifetime === 'object' ? { ...p.lifetime } : {};
    const daily =
      p.daily !== null &&
      typeof p.daily === 'object' &&
      typeof p.daily?.date === 'string'
        ? {
            date: p.daily.date,
            progress:
              p.daily.progress !== null && typeof p.daily.progress === 'object'
                ? { ...p.daily.progress }
                : {},
            claimed: Array.isArray(p.daily.claimed)
              ? p.daily.claimed.filter((id): id is string => typeof id === 'string')
              : [],
          }
        : empty.daily;
    return { unlocked, lifetime, daily };
  } catch {
    return empty;
  }
}

/** Roll the daily bucket when the UTC date changed (implicit reset). */
export function rollDailyIfNeeded(q: QuestState, now: number): boolean {
  const today = utcDateKey(now);
  if (q.daily.date === today) return false;
  q.daily = { date: today, progress: {}, claimed: [] };
  return true;
}

export interface QuestEvaluation {
  unlockedNow: AchievementDef[];
  completedNow: DailyDef[];
}

/**
 * Apply a metric delta (or absolute value for 'tier'/'surviveSec') and
 * return what just unlocked/completed. Mutates `q` in place; the caller
 * persists + credits rewards + broadcasts.
 */
export function applyMetric(
  q: QuestState,
  metric: QuestMetric,
  value: number,
  now: number,
  mode: 'add' | 'max' = 'add',
): QuestEvaluation {
  rollDailyIfNeeded(q, now);
  const prevLifetime = q.lifetime[metric] ?? 0;
  q.lifetime[metric] =
    mode === 'add' ? prevLifetime + value : Math.max(prevLifetime, value);

  const unlockedNow: AchievementDef[] = [];
  for (const ach of ACHIEVEMENTS) {
    if (ach.metric !== metric) continue;
    if (q.unlocked.includes(ach.id)) continue;
    if ((q.lifetime[metric] ?? 0) >= ach.target) {
      q.unlocked.push(ach.id);
      unlockedNow.push(ach);
    }
  }

  const completedNow: DailyDef[] = [];
  const prevDaily = q.daily.progress[metric] ?? 0;
  q.daily.progress[metric] =
    mode === 'add' ? prevDaily + value : Math.max(prevDaily, value);
  for (const daily of dailiesFor(q.daily.date)) {
    if (daily.metric !== metric) continue;
    if (q.daily.claimed.includes(daily.id)) continue;
    if ((q.daily.progress[metric] ?? 0) >= daily.target) {
      q.daily.claimed.push(daily.id);
      completedNow.push(daily);
    }
  }
  return { unlockedNow, completedNow };
}
